import { useEffect, useRef, useState } from "react";
import { api, peso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Camera, ArrowSquareIn, ArrowSquareOut, CheckCircle, QrCode } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function Sessions() {
  const { user } = useAuth();
  const [plate, setPlate] = useState("");
  const [confidence, setConfidence] = useState(null);
  const [recognizing, setRecognizing] = useState(false);
  const [imgPreview, setImgPreview] = useState(null);
  const [imgB64, setImgB64] = useState(null);
  const [active, setActive] = useState([]);
  const [busy, setBusy] = useState(false);
  const [paymentForExit, setPaymentForExit] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/sessions/active").then(r => setActive(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (f) => {
    if (!f) return;
    const dataUri = await fileToBase64(f);
    setImgPreview(dataUri);
    setImgB64(dataUri);
  };

  const runALPR = async () => {
    if (!imgB64) {
      toast.error("Capture or select an image first");
      return;
    }
    setRecognizing(true);
    try {
      const r = await api.post("/alpr/recognize", { image_base64: imgB64 });
      if (r.data.plate) {
        setPlate(r.data.plate);
        setConfidence(r.data.confidence);
        toast.success(`Plate recognized: ${r.data.plate} (${Math.round(r.data.confidence * 100)}%)`);
      } else {
        toast.warning("Could not read plate — please enter manually");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recognition failed");
    } finally {
      setRecognizing(false);
    }
  };

  const entry = async () => {
    if (!plate) { toast.error("Plate required"); return; }
    setBusy(true);
    try {
      // try lookup visitor pre-reg
      let visitorId = null;
      try {
        const v = await api.get(`/visitors/lookup?plate=${encodeURIComponent(plate)}`);
        if (v.data) visitorId = v.data.id;
      } catch {}
      const r = await api.post("/sessions/entry", {
        plate_number: plate,
        property_id: user?.property_id,
        entry_image: imgB64,
        plate_confidence: confidence,
        visitor_id: visitorId,
      });
      toast.success(`Entry recorded — ${r.data.session_type}${r.data.slot_id ? ` · slot assigned` : ""}`);
      setPlate(""); setConfidence(null); setImgPreview(null); setImgB64(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Entry failed");
    } finally { setBusy(false); }
  };

  const exitSession = async (s) => {
    setBusy(true);
    try {
      const r = await api.post("/sessions/exit", { session_id: s.id });
      if (r.data.fee > 0) {
        const pay = await api.post("/payments/qr", { session_id: s.id });
        setPaymentForExit({ session: r.data, payment: pay.data });
        toast.info(`Exit recorded — ${peso(r.data.fee)} due`);
      } else {
        toast.success("Exit recorded — no fee");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Exit failed");
    } finally { setBusy(false); }
  };

  const confirmPayment = async () => {
    if (!paymentForExit) return;
    try {
      await api.post("/payments/confirm", { payment_id: paymentForExit.payment.id });
      toast.success("Payment confirmed · session closed");
      setPaymentForExit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Gate Operations</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">Entry & exit.</h1>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-[#141414] border-zinc-800 p-6">
          <div className="flex items-center gap-2">
            <ArrowSquareIn size={20} className="text-emerald-400" />
            <h3 className="font-heading text-xl font-bold">Record Entry</h3>
          </div>
          <p className="text-sm text-zinc-400 mt-1">Snap a photo of the plate, or type it manually.</p>

          <div className="mt-4 space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid={TID.alprUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              variant="outline"
              className="w-full min-h-[48px] border-zinc-700 bg-[#0F0F10] hover:bg-zinc-800"
              data-testid="alpr-capture-button"
            >
              <Camera size={18} className="mr-2" /> {imgPreview ? "Retake / change photo" : "Capture plate photo"}
            </Button>
            {imgPreview && (
              <img src={imgPreview} alt="plate" className="w-full max-h-48 object-cover rounded-md border border-zinc-800" />
            )}
            {imgB64 && (
              <Button onClick={runALPR} disabled={recognizing} className="w-full min-h-[48px] bg-emerald-500 text-black hover:bg-emerald-400" data-testid={TID.alprRun}>
                {recognizing ? "Reading plate…" : "Run AI Plate Recognition"}
              </Button>
            )}
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plate Number</Label>
              <Input
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="ABC-1234"
                className="bg-[#0F0F10] border-zinc-800 font-mono text-lg min-h-[48px] mt-2"
                data-testid={TID.entryPlate}
              />
              {confidence != null && (
                <p className="text-[10px] font-mono text-zinc-500 mt-1">AI confidence: {Math.round(confidence * 100)}%</p>
              )}
            </div>
            <Button
              onClick={entry}
              disabled={busy || !plate}
              className="w-full min-h-[48px] bg-white text-black hover:bg-zinc-200"
              data-testid={TID.entrySubmit}
            >
              <ArrowSquareIn size={18} className="mr-2" /> Record Entry
            </Button>
          </div>
        </Card>

        <Card className="bg-[#141414] border-zinc-800 p-6">
          <div className="flex items-center gap-2">
            <ArrowSquareOut size={20} className="text-orange-400" />
            <h3 className="font-heading text-xl font-bold">Active Sessions</h3>
          </div>
          <p className="text-sm text-zinc-400 mt-1">{active.length} currently inside</p>
          <div className="mt-4 space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {active.map(s => (
              <div key={s.id} className="bg-[#0F0F10] border border-zinc-800 rounded-md p-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-lg">{s.plate_number}</p>
                  <p className="text-[10px] font-mono uppercase text-zinc-500">
                    {s.session_type} · entered {new Date(s.entry_time).toLocaleTimeString()}
                  </p>
                </div>
                <Button
                  onClick={() => exitSession(s)}
                  disabled={busy}
                  className="bg-orange-500 text-black hover:bg-orange-400"
                  data-testid={`exit-session-${s.plate_number}`}
                >
                  Exit
                </Button>
              </div>
            ))}
            {active.length === 0 && <p className="text-sm text-zinc-500">No active sessions.</p>}
          </div>
        </Card>
      </div>

      {paymentForExit && (
        <Card className="bg-[#141414] border-zinc-800 p-6">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={20} className="text-amber-300" />
            <h3 className="font-heading text-xl font-bold">Payment Required</h3>
          </div>
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="bg-white p-4 rounded-md">
              <QRCodeSVG value={paymentForExit.payment.qr_payload} size={180} />
            </div>
            <div className="space-y-2 flex-1">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Reference</p>
              <p className="font-mono">{paymentForExit.payment.reference}</p>
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-3">Amount</p>
              <p className="font-mono text-4xl text-amber-300">{peso(paymentForExit.session.fee)}</p>
              <Button onClick={confirmPayment} className="mt-3 bg-emerald-500 text-black hover:bg-emerald-400 min-h-[44px]" data-testid={TID.payConfirm(paymentForExit.payment.id)}>
                <CheckCircle size={18} className="mr-2" /> Confirm Payment Received
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
